/**
 * Campos de formulario (contrato 3.1, `field_completed` y `page_exited.last_field`):
 * escuchas globales de focusin, input y focusout en los input, textarea y
 * select de la app. Nunca se lee el valor: solo el nombre del campo (`name` o
 * `id`, sin dígitos finales), cuánto tiempo tuvo el foco y cuántas ediciones y
 * borrados hubo.
 *
 * Fuera: los de tipo password y hidden, los marcados (o dentro de algo
 * marcado) con `data-track-ignore` y los que no tienen un nombre que cumpla el
 * tipo `field` del contrato (ids generados como `mui-12` o `react-select-3-input`).
 */
import { fieldNameOf, isFieldName } from "./events";
import type { FieldCompletion } from "./tracker";

export interface FieldWatcherTarget {
  fieldFocused: (field: string) => void;
  fieldCompleted: (completion: FieldCompletion) => void;
}

export interface FieldWatcherOptions {
  doc?: Document;
  now?: () => number;
}

const TRACKED_TAGS = ["INPUT", "TEXTAREA", "SELECT"];
const IGNORED_TYPES = ["password", "hidden"];

/** Nombre de campo que se puede mandar, o null si el campo no se sigue. */
export const trackedFieldName = (target: EventTarget | null): string | null => {
  const el = target as Element | null;
  if (!el || typeof el.tagName !== "string" || TRACKED_TAGS.indexOf(el.tagName) < 0) return null;
  const type = (el.getAttribute("type") || "").toLowerCase();
  if (IGNORED_TYPES.indexOf(type) >= 0) return null;
  if (typeof el.closest === "function" && el.closest("[data-track-ignore]")) return null;
  const raw = el.getAttribute("name") || el.getAttribute("id") || "";
  if (!raw) return null;
  const name = fieldNameOf(raw);
  return isFieldName(name) ? name : null;
};

interface ActiveField {
  el: EventTarget;
  field: string;
  focusedAt: number;
  edits: number;
  corrections: number;
}

export const installFieldWatchers = (
  target: FieldWatcherTarget,
  options: FieldWatcherOptions = {}
): (() => void) => {
  const doc = options.doc ?? document;
  const now = options.now ?? (() => Date.now());
  let active: ActiveField | null = null;

  const onFocusIn = (event: Event) => {
    try {
      const field = trackedFieldName(event.target);
      if (!field || !event.target) return;
      active = { el: event.target, field, focusedAt: now(), edits: 0, corrections: 0 };
      target.fieldFocused(field);
    } catch {
      /* el tracking nunca rompe la app */
    }
  };

  const onInput = (event: Event) => {
    if (!active || event.target !== active.el) return;
    active.edits += 1;
    const inputType = (event as InputEvent).inputType || "";
    if (inputType.indexOf("delete") === 0) active.corrections += 1;
  };

  const onFocusOut = (event: Event) => {
    if (!active || event.target !== active.el) return;
    const done = active;
    active = null;
    if (done.edits === 0) return;
    try {
      target.fieldCompleted({
        field: done.field,
        time_ms: Math.max(0, now() - done.focusedAt),
        edits: done.edits,
        corrections: done.corrections,
      });
    } catch {
      /* noop */
    }
  };

  doc.addEventListener("focusin", onFocusIn, true);
  doc.addEventListener("input", onInput, true);
  doc.addEventListener("focusout", onFocusOut, true);
  return () => {
    doc.removeEventListener("focusin", onFocusIn, true);
    doc.removeEventListener("input", onInput, true);
    doc.removeEventListener("focusout", onFocusOut, true);
  };
};
